import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { API_DOCS } from './MieAttivitaAdozionePage.api';
import {
  MiaAttivita, StatoAttivita, TipoFeedback, GruppoScadenza,
  STATO_ATTIVITA_LABEL, TIPO_FEEDBACK_LABEL, GRUPPO_LABEL, ORDINE_GRUPPI,
  fmtData, gruppoDi, giorniAllaScadenza,
} from './adozione';

/**
 * Le mie attività di adozione, su tutte le società in cui opero.
 *
 * Nessun filtro società, di proposito: chi segue più società deve vedere tutto
 * insieme senza cambiare la topbar. Il raggruppamento è per scadenza e non per
 * società perché la domanda che ci si fa aprendo questa pagina è "cosa devo
 * fare adesso", non "in quale azienda".
 */
export default function MieAttivitaAdozionePage() {
  const [attivita, setAttivita] = useState<MiaAttivita[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [selezionata, setSelezionata] = useState<MiaAttivita | null>(null);

  const carica = useCallback(async () => {
    setIsLoading(true);
    setErrore('');
    try {
      // @api GET /api/adozione/mie-attivita -> {"success":true,"data":[{"id":1,"titolo":"Inserire i primi 10 ordini","descrizione":null,"scadenza":"2026-10-15","stato":"DA_FARE","notaAssegnatario":null,"completatoAt":null,"inRitardo":false,"obiettivo":{"id":1,"titolo":"Ciclo attivo operativo"},"piano":{"id":1,"titolo":"Adozione BOP 2026","societaId":1,"dataFine":"2026-12-10","societa":{"id":1,"nome":"Pianinvest"}}}]}
      const res = await apiFetch<{ data: MiaAttivita[] }>('/api/adozione/mie-attivita');
      setAttivita(res.data);
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const gruppi = useMemo(() => {
    const mappa = new Map<GruppoScadenza, MiaAttivita[]>();
    for (const g of ORDINE_GRUPPI) mappa.set(g, []);
    for (const a of attivita) mappa.get(gruppoDi(a))!.push(a);
    return mappa;
  }, [attivita]);

  const aperte = attivita.filter(a => a.stato !== 'COMPLETATA' && a.stato !== 'NON_APPLICABILE').length;
  const inRitardo = gruppi.get('RITARDO')?.length ?? 0;

  return (
    <Layout title="Le mie attività">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Le mie attività</h1>
          <p className="bop-page-subtitle">
            Quello che ti è stato assegnato nei piani di adozione, in tutte le società in cui lavori.
          </p>
        </div>
      </div>

      {errore && <p className="bop-text-danger">{errore}</p>}
      {isLoading && <p className="bop-text-muted">Caricamento…</p>}

      {!isLoading && attivita.length === 0 && (
        <div className="bop-empty-state">
          <p className="bop-empty-state__title">Nessuna attività assegnata</p>
          <p className="bop-empty-state__text">
            Non hai attività nei piani di adozione attivi. Quando te ne verranno assegnate le troverai qui.
          </p>
        </div>
      )}

      {!isLoading && attivita.length > 0 && (
        <>
          <div className="bop-adozione__stats">
            <div className="bop-adozione__stat">
              <div className="bop-adozione__stat-label">Da fare</div>
              <div className="bop-adozione__stat-value">{aperte}</div>
              <div className="bop-adozione__stat-sub">su {attivita.length} assegnate</div>
            </div>
            <div className={`bop-adozione__stat${inRitardo > 0 ? ' bop-adozione__stat--allarme' : ''}`}>
              <div className="bop-adozione__stat-label">In ritardo</div>
              <div className="bop-adozione__stat-value">{inRitardo}</div>
            </div>
          </div>

          {ORDINE_GRUPPI.map(g => {
            const righe = gruppi.get(g) ?? [];
            if (righe.length === 0) return null;
            return (
              <div className="bop-adozione__gruppo" key={g}>
                <h2 className={`bop-adozione__gruppo-titolo${g === 'RITARDO' ? ' bop-adozione__gruppo-titolo--ritardo' : ''}`}>
                  {GRUPPO_LABEL[g]}
                  <span className="bop-adozione__gruppo-conteggio">{righe.length}</span>
                </h2>
                <div className="bop-card">
                  <table className="bop-table bop-table--clickable">
                    <thead>
                      <tr>
                        <th>Attività</th>
                        <th>Obiettivo</th>
                        <th>Società</th>
                        <th>Scadenza</th>
                        <th>Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {righe.map(a => (
                        <tr key={a.id} onClick={() => setSelezionata(a)}>
                          <td>{a.titolo}</td>
                          <td className="bop-text-muted">{a.obiettivo.titolo}</td>
                          <td className="bop-text-muted">{a.piano.societa.nome}</td>
                          <td className={a.inRitardo ? 'bop-adozione__scadenza--ritardo' : undefined}>
                            <EtichettaScadenza scadenza={a.scadenza} />
                          </td>
                          <td>
                            <span className={`bop-badge bop-badge--${STATO_ATTIVITA_LABEL[a.stato].variant}`}>
                              {STATO_ATTIVITA_LABEL[a.stato].label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}

      {selezionata && (
        <PannelloAttivita
          attivita={selezionata}
          onChiudi={() => setSelezionata(null)}
          onSalvato={async () => { setSelezionata(null); await carica(); }}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

function EtichettaScadenza({ scadenza }: { scadenza: string | null }) {
  const giorni = giorniAllaScadenza(scadenza);
  if (giorni === null) return <>—</>;
  if (giorni === 0) return <>Oggi</>;
  if (giorni === 1) return <>Domani</>;
  if (giorni < 0) return <>{fmtData(scadenza)} ({-giorni} gg fa)</>;
  return <>{fmtData(scadenza)}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pannello: cambia stato, lascia una nota, scrivi un feedback
//
// Il feedback si scrive qui perché è il momento in cui l'attrito è fresco: è
// da questa schermata che arriverà il materiale più utile del rollout.
// ─────────────────────────────────────────────────────────────────────────────

function PannelloAttivita({ attivita, onChiudi, onSalvato }: {
  attivita: MiaAttivita;
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  const [stato, setStato] = useState<StatoAttivita>(attivita.stato);
  const [nota, setNota] = useState(attivita.notaAssegnatario ?? '');
  const [tipoFeedback, setTipoFeedback] = useState<TipoFeedback>('DIFFICOLTA');
  const [testoFeedback, setTestoFeedback] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [errore, setErrore] = useState('');

  const salva = async () => {
    setSalvando(true);
    setErrore('');
    try {
      // @api PUT /api/adozione/attivita/1/stato {"stato":"IN_CORSO","notaAssegnatario":"iniziato oggi"} -> {"success":true}
      await apiFetch(`/api/adozione/attivita/${attivita.id}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato, notaAssegnatario: nota || null }),
      });

      if (testoFeedback.trim()) {
        // @api POST /api/adozione/feedback {"attivitaId":1,"tipo":"DIFFICOLTA","testo":"..."} -> {"success":true,"data":{"id":1}}
        await apiFetch('/api/adozione/feedback', {
          method: 'POST',
          body: JSON.stringify({ attivitaId: attivita.id, tipo: tipoFeedback, testo: testoFeedback }),
        });
      }

      onSalvato();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bop-modal-overlay" onClick={onChiudi}>
      <div className="bop-modal bop-modal--md" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">{attivita.titolo}</h2>
          <button className="bop-modal__close" onClick={onChiudi} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body">
          <div className="bop-adozione__obiettivo-meta">
            <span>{attivita.piano.societa.nome}</span>
            <span>·</span>
            <span>{attivita.obiettivo.titolo}</span>
            {attivita.scadenza && <span>· entro il {fmtData(attivita.scadenza)}</span>}
          </div>

          {attivita.descrizione && <p className="bop-text-muted">{attivita.descrizione}</p>}

          <div className="bop-field">
            <label className="bop-field__label" htmlFor="att-stato">A che punto sei</label>
            <select id="att-stato" className="bop-field__select" value={stato}
              onChange={e => setStato(e.target.value as StatoAttivita)}>
              {(Object.keys(STATO_ATTIVITA_LABEL) as StatoAttivita[]).map(s => (
                <option key={s} value={s}>{STATO_ATTIVITA_LABEL[s].label}</option>
              ))}
            </select>
            <p className="bop-field__hint">
              Se l&apos;attività non ti riguarda, scegli &quot;Non applicabile&quot; invece di lasciarla aperta:
              nel monitoraggio viene contata a parte, non come lavoro fatto.
            </p>
          </div>

          <div className="bop-field">
            <label className="bop-field__label" htmlFor="att-nota">Nota</label>
            <textarea id="att-nota" className="bop-field__textarea" rows={2} value={nota}
              onChange={e => setNota(e.target.value)}
              placeholder="Cosa hai fatto, cosa manca…" />
          </div>

          <div className="bop-section-divider" />

          <div className="bop-field">
            <label className="bop-field__label" htmlFor="att-fb-tipo">Vuoi lasciare un feedback?</label>
            <select id="att-fb-tipo" className="bop-field__select" value={tipoFeedback}
              onChange={e => setTipoFeedback(e.target.value as TipoFeedback)}>
              {(Object.keys(TIPO_FEEDBACK_LABEL) as TipoFeedback[]).map(t => (
                <option key={t} value={t}>{TIPO_FEEDBACK_LABEL[t].label}</option>
              ))}
            </select>
          </div>
          <div className="bop-field">
            <textarea className="bop-field__textarea" rows={3} value={testoFeedback}
              onChange={e => setTestoFeedback(e.target.value)}
              placeholder="Cosa ti ha rallentato, cosa cambieresti, cosa non era chiaro…"
              aria-label="Testo del feedback" />
            <p className="bop-field__hint">
              Facoltativo. Lo leggono le persone che seguono il piano nella tua società.
              Per un malfunzionamento del sistema conviene invece aprire una richiesta di assistenza.
            </p>
          </div>

          {errore && <p className="bop-field__error">{errore}</p>}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onChiudi}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>
            {salvando ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}
